import type { AccountType, ActionButtonsVisibility } from '@/entities/account';
import { useTranslation } from 'react-i18next';
import { openTradingAccUrl, useMediaQuery } from '@/shared/lib';
import { useCallback } from 'react';
import { ChevronRightIcon, EllipsisVerticalIcon } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItemWithSubmenu,
  DropdownMenuTrigger,
} from '@/shared/components';
import ArrowDownIcon from '@/shared/assets/arrow-down-icon.svg?react';
import ArrowTopRightIcon from '@/shared/assets/arrow-top-right-icon.svg?react';
import ExchangeProcessIcon from '@/shared/assets/exchange-process-icon.svg?react';
import ArrowsDownUpIcon from '@/shared/assets/arrows-down-up-icon.svg?react';
import CandlesIcon from '@/shared/assets/candles-icon.svg?react';
import { ActionMenuItem } from '@/pages/main';
import { ActionMenuItemContent } from '@/pages/main/ui/accounts-carousel/ActionMenuItemContent.tsx';
import { cn } from '@/shared/utils';

const TRIGGER_CLASSNAME =
  'cursor-pointer p-0 relative dark:bg-transparent hover:bg-stroke rounded-full w-[4.4rem] h-[4.4rem] flex items-center justify-center [&>div]:flex [&>div]:items-center [&>div]:justify-center';

type Props = {
  open: boolean;
  setOpen: (open: boolean) => void;
  accountType: AccountType;
  accountNumber: string;
  tradingAccKey?: number;
  actionButtonsVisibility: ActionButtonsVisibility;
  openDepositModal?: () => void;
  openWithdrawModal?: () => void;
  openInternalTransferFormModal?: (props?: {
    accountType: AccountType;
    accountNumber: string;
    assetCode: string;
    direction: 'from' | 'to';
  }) => void;
  onExchange: (assetCode: string) => void;
  code: string;
  available: number;
};

export const AccountAssetsListItemMenu = ({
  open,
  setOpen,
  accountType,
  accountNumber,
  tradingAccKey,
  actionButtonsVisibility,
  openInternalTransferFormModal,
  openWithdrawModal,
  openDepositModal,
  onExchange,
  code,
  available,
}: Props) => {
  const isMobile = useMediaQuery('(max-width: 1024px)');
  const { t } = useTranslation();

  const { deposit, trade, withdraw, exchange, transfer } =
    actionButtonsVisibility;

  const closeAnd = useCallback(
    (fn?: () => void) => () => {
      if (fn) {
        fn();
      }
      setOpen(false);
    },
    []
  );

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger
        withChevron={false}
        wrapperClassName={isMobile ? '-ml-[0.8rem]' : undefined}
        className={cn(
          isMobile
            ? 'w-0 h-0 p-0 overflow-hidden opacity-0 pointer-events-none border-0'
            : cn(TRIGGER_CLASSNAME, open ? 'bg-stroke' : '')
        )}>
        {!isMobile && <EllipsisVerticalIcon className="size-[2.4rem]" />}
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        side="top"
        alignOffset={-16}
        sideOffset={8}
        className="w-[23.9rem] min-w-[23.9rem] p-0 rounded-[1.6rem]"
        classNameViewport="w-full max-w-full p-[0.8rem]">
        {deposit && (
          <ActionMenuItem
            icon={ArrowDownIcon}
            label={t('personalAccount.buttons.deposit')}
            onClick={closeAnd(() => openDepositModal?.())}
          />
        )}
        {withdraw && (
          <ActionMenuItem
            icon={ArrowTopRightIcon}
            label={t('personalAccount.buttons.withdraw')}
            onClick={closeAnd(() => openWithdrawModal?.())}
          />
        )}
        {exchange && (
          <ActionMenuItem
            icon={ExchangeProcessIcon}
            label={t('personalAccount.buttons.exchange')}
            onClick={closeAnd(() => onExchange(code))}
          />
        )}
        {transfer && (
          <DropdownMenuItemWithSubmenu
            className="h-[4.8rem] w-full rounded-[1.2rem] cursor-pointer pr-[0.5rem]"
            onClick={e => e.stopPropagation()}
            renderTrigger={() => (
              <ActionMenuItemContent
                icon={ArrowsDownUpIcon}
                label={t('personalAccount.buttons.transfer')}>
                <div className="flex items-center justify-center w-[2rem] h-[2rem]">
                  <ChevronRightIcon className="size-full text-tertiary stroke-[3]" />
                </div>
              </ActionMenuItemContent>
            )}
            sideOffset={isMobile ? -230 : 10} //8
            alignOffset={isMobile ? 56 : available > 0 ? -64 : -8} //available > 0 ? -64 : -8
            avoidCollisions={!isMobile}
            subContentClassName="z-2 relative">
            <div className="bg-popover min-w-[18.0rem] gap-[0.8 rem] rounded-[1.6rem] p-[0.8rem] flex flex-col border shadow-md">
              <ActionMenuItem
                label={t('personalAccount.buttons.transferSide.from')}
                childrenClassName={'px-[1.2rem]'}
                onClick={closeAnd(() =>
                  openInternalTransferFormModal?.({
                    accountType,
                    accountNumber,
                    assetCode: code,
                    direction: 'to',
                  })
                )}
              />
              {available > 0 && (
                <ActionMenuItem
                  label={t('personalAccount.buttons.transferSide.to')}
                  childrenClassName={'px-[1.2rem]'}
                  onClick={closeAnd(() =>
                    openInternalTransferFormModal?.({
                      accountType,
                      accountNumber,
                      assetCode: code,
                      direction: 'from',
                    })
                  )}
                />
              )}
            </div>
          </DropdownMenuItemWithSubmenu>
        )}

        {trade && (
          <ActionMenuItem
            icon={CandlesIcon}
            label={t('personalAccount.buttons.trade')}
            onClick={closeAnd(() =>
              openTradingAccUrl(String(tradingAccKey), accountType)
            )}
          />
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
